import { API } from '~/api';
import { Gender, Occasion, Sillage, Weather } from '~/api/api.declaration';
import { BrandCreateDto } from '~/shared/dto/admin/brand.dto';
import { FeedbackReplyDto } from '~/shared/dto/admin/feedback.dto';
import {
	FragranceCreateDto,
	FragranceLinkType,
	FragranceListFilterFieldsDto,
	FragranceModerationDto,
	FragranceOfferDto,
	FragranceSetSuperDto,
} from '~/shared/dto/admin/fragrance.dto';
import { IngredientCreateDto } from '~/shared/dto/admin/ingredient.dto';
import { NoteCreateDto } from '~/shared/dto/admin/note.dto';
import { PerfumerCreateDto } from '~/shared/dto/admin/perfumer.dto';
import { PostCreateDto } from '~/shared/dto/admin/post.dto';
import {
	PromotionBestsellerDto,
	PromotionCreateDto,
	PromotionNewbieDto,
	PromotionOfferDto,
} from '~/shared/dto/admin/promotion.dto';
import {
	PushCampaignCreateDto,
	PushExperienceRatingCondition,
	PushFragranceShelvesCondition,
	PushUserCondition,
} from '~/shared/dto/admin/push.campaign.dto';
import { MaintenanceDto, SettingsDto, StopWordsDto } from '~/shared/dto/admin/settings.dto';
import { StaffListFilterFieldsDto, StaffUpdateDto } from '~/shared/dto/admin/staff.dto';
import { UserMessageDto } from '~/shared/dto/admin/user.dto';
import { Concentration, FragranceModerationStatus, Longevity, Season } from '~/shared/dto/model.enums';
import { Tr } from '.';

export function mapTr<T extends object>(texts: Required<{ [P in keyof T]: string }>) {
	return texts;
}

const en = {
	common: {
		test: 'string',
		param: 'param: {{param}}' as Tr<{ test: string }>,
	},
	errors: {
		ERR_STAFF_NOT_APPROVED: 'Your account is not approved. Please wait for approval.',
		ERR_RECALCULATE_IN_PROGRESS: 'Fragrance impressions recalculation is already in progress.',
	},
	validation: {
		minLength: 'The field must contain at least {{arg1}} characters' as Tr<{ arg1: number }>,
		maxLength: 'The field must contain no more than {{arg1}} characters' as Tr<{ arg1: number }>,
		matches: 'The field must contain only {{arg1}}' as Tr<{ arg1: string }>,
	},
	dashboard: {
		actions: 'Actions',
		actionsSeeAll: 'See All',
		noActions: 'No user actions',
	},
	dashboardPeriods: {
		1: '24 hours',
		7: '7 days',
		30: '30 days',
	},
	dashboardCardTitle: {
		...mapTr<API.AnalyticsByPeriod & API.AnalyticsGeneralBase & API.AnalyticsGeneralUsers & API.AnalyticsUGC>({
			// By Period
			newUsers: 'Registered users',
			actionTotal: 'Initiated Actions',
			actionUsers: 'Active users',
			actionAverage: 'Average actions per user',
			churn: 'Churn rate',
			featurePenetration: 'Feature interactions',
			featureUsers: 'Feature users',
			retention: 'Retention',
			returningUsers: 'Returned users',
			recovery: 'Churn recovery',
			deletedUsers: 'Deleted users',

			// Common Users
			users: 'Total users',
			locationGranted: 'Location granted',
			pushGranted: 'Push granted',
			stickinessRatio: 'Stickiness ratio',
			communityGrowth: 'Community growth',

			// Common Base
			brands: 'Brands',
			fragrances: 'Fragrances',
			perfumers: 'Perfumers',

			// UGC
			fragrancesWithExperience: 'Perfumes on shelves',
			reviews: 'Reviews',
			fragrancesWithReview: 'Perfumes with review',
			posts: 'Posts',
			postsWithText: 'Posts with text',
			postComments: 'Post comments',
			layerings: 'Layerings',
			fragrancesWithLayering: 'Perfumes with layering',
			fragrancesWithNote: 'Perfumes with note',
			referrals: 'Invited users',
		}),
	},
	dashboardCardHelpers: {
		...mapTr<API.AnalyticsByPeriod & API.AnalyticsGeneralBase & API.AnalyticsGeneralUsers & API.AnalyticsUGC>({
			actionUsers: 'Unique users who performed at least one action during the selected period',
			actionTotal: 'Total count of all actions performed by users during the selected period',
			featureUsers:
				'Unique users who engaged with key features like posts, reviews, ratings, or notes during the selected period',
			actionAverage: 'Total actions divided by active users during the selected period',
			featurePenetration: 'Number of users who interacted with key features (posts, reviews, ratings, or notes)',
			newUsers: 'Number of new user accounts created during the selected period',
			deletedUsers: 'Number of user accounts deleted during the selected period',
			returningUsers:
				'Active users minus registered users in the selected period (users who registered before and remained active)',
			churn: 'Percentage of all users (from last 60 days) who had no activity during the selected period',
			recovery:
				'Percentage of users (from last 60 days) who had no activity in the previous period but became active in the current period',
			retention:
				'Percentage of registered users (from last 60 days) who had activity exactly N days after registration (where N = selected period)',
			// From common
			users: 'Total number of registered users in the system',
			locationGranted: 'Total number of users who have granted location access',
			pushGranted: 'Total number of users who have granted push notifications access',
			communityGrowth: 'Growth of the user community over time, as percentage',
			stickinessRatio:
				'Daily active users (last 24h) divided by monthly active users (last 30 days), as percentage',
			brands: 'Total number of fragrance brands in the system',
			fragrances: 'Total number of fragrances in the system',
			perfumers: 'Total number of perfumers in the system',
			// UGC
			fragrancesWithExperience: "Number of unique perfumes added to users' shelves",
			reviews: 'Number of reviews written',
			fragrancesWithReview: 'Number of unique perfumes for which reviews were written',
			posts: 'Number of published SOTDs',
			postsWithText: 'Number of SOTDs with text',
			postComments: 'Number of comments written on SOTDs',
			layerings: 'Number of layering added',
			fragrancesWithLayering: 'Number of unique perfumes to which layering was added',
			fragrancesWithNote: 'Number of perfumes in which notes were noted',
			referrals: 'Number of invited friends',
		}),
	},
	dashboardActivity: {
		...mapTr<{ [P in API.UserActivityType]: string }>({
			SHELF: 'Shelf',
			FEEDBACK: 'Feedback',
			FRAGRANCE: 'Fragrance',
			USER: 'User',
			USER_AVATAR: 'User Avatar',
			USERRELATION: 'User Relation',
			POST: 'Post',
			POSTCOMMENT: 'Post Comment',
			POSTREACTION: 'Post Reaction',
			CUSTOMNOTE: 'Custom Note',
			NOTEBYUSER: 'Note by User',
			EXPERIENCE_BATCHCODE: 'Experience Batchcode',
			EXPERIENCE_LONGEVITY: 'Experience Longevity',
			EXPERIENCE_OCCASION: 'Experience Occasion',
			EXPERIENCE_SEASON: 'Experience Season',
			EXPERIENCE_SILLAGE: 'Experience Sillage',
			EXPERIENCE_WEATHER: 'Experience Weather',
			EXPERIENCELAYERING: 'Experience Layering',
			EXPERIENCEREMIND: 'Experience Remind',
			USERDEVICE_PUSH: 'User Push',
			USERDEVICE_LOCATION: 'User Location',
			USERDEVICE_NOTIFICATION: 'User Notification',
			EXPERIENCE: 'Experience',
			EXPERIENCE_RATING: 'Experience Rating',
			EXPERIENCE_REVIEW: 'Experience Review',
		}),
	},
	dashboardActivityAction: {
		GET: 'View',
		CREATE: 'Add',
		UPDATE: 'Update',
		DELETE: 'Delete',
		LIKE: 'Like',
		UNLIKE: 'Unlike',
		FOLLOW: 'Follow',
		UNFOLLOW: 'Unfollow',
		BLOCK: 'Block',
		DENIED: 'Denied',
		GRANTED: 'Granted',
		CONTACT: 'Contact',
	},
	feedbackReply: {
		titles: ['Hi, {{userName}}!', 'Hello, {{userName}}!', 'Dear {{userName}}!', 'Greetings, {{userName}}!'],
		messages: [
			'{{fragranceName}} {{brandName}} is already in the catalog. Thank you for staying with us!',
			'Your wish come true! We added the {{fragranceName}} {{brandName}} to the catalogue.',
			'You can find {{fragrance}} {{brand}} in our catalog under its new name, {{fragranceName}} {{brandName}}. The name was changed in {{year}}. Thank you for staying with us!',
			'Thanks for suggesting {{fragrance}} {{brand}}! We have yet to find any info. If you have reliable sources, plz share the link with us via the contact form: Profile-settings-contact us. Thanks a lot!',
		],
	},
	dto: {
		PostCreateDto: {
			...mapTr<PostCreateDto>({
				city: 'City',
				country: 'Country',
				fragranceId: 'Fragrance',
				publishAt: 'Publish At, your local time',
				rating: 'Rating',
				temperature: 'Temperature, degrees',
				text: 'Post text',
				userId: 'Created by',
				weather: 'Weather',
			}),
		},
		StaffUpdateDto: {
			...mapTr<StaffUpdateDto>({
				expiresAt: 'Expire at',
				name: 'Name',
				status: 'Status',
				brandPermission: 'Brand permissions',
				fragrancePermission: 'Fragrance permissions',
				perfumersPermission: 'Perfumers permissions',
				notesPermission: 'Notes & Ingredients permissions',
				staffPermission: 'Staff permissions',
				feedbackPermission: 'Feedback permissions',
				userPermission: 'Users permissions',
				pushPermission: 'Push permissions',
				snapshotPermission: 'Snapshot permissions',
				settingsPermission: 'Settings permissions',
				postPermission: 'Post permissions',
				postCommentPermission: 'Post comment permissions',
				partnerPermission: 'Partner permissions',
				promotionPermission: 'Promotion permissions',
				analyticsPermission: 'Analytics permissions',
			}),
		},
		StaffListFilterFieldsDto: {
			...mapTr<StaffListFilterFieldsDto>({
				status: 'Status',
			}),
		},
		BrandCreateDto: {
			...mapTr<BrandCreateDto>({
				id: 'ID',
				name: 'Name',
				active: 'Active (visible to customers)',
				country: 'Country',
				description: 'Description',
				synonyms: 'Synonyms',
				images: 'Images',
				comment: 'Put some words about your changes',
				year: 'Year',
				dupe: 'Dupe (brand is a duplicate)',
			}),
		},
		PromotionCreateDto: {
			...mapTr<PromotionCreateDto>({
				brandId: 'Brand',
				description: 'Description',
				activeFrom: 'Active From',
				activeTo: 'Active To',
				brandColors: 'Brand Colors',
				logo: 'Logo (Light Theme)',
				logoDark: 'Logo (Dark Theme)',
				bannerImage: 'Banner Image',
				offers: 'Offers',
				bestsellers: 'Bestsellers',
				newbies: 'Newbies',
			}),
		},
		PromotionOfferDto: {
			...mapTr<PromotionOfferDto>({
				id: 'ID',
				fragranceId: 'Fragrance',
				order: 'Order',
				storeOfferId: 'Store Offer',
			}),
		},
		PromotionBestsellerDto: {
			...mapTr<PromotionBestsellerDto>({
				id: 'ID',
				fragranceId: 'Fragrance',
				order: 'Order',
			}),
		},
		PromotionNewbieDto: {
			...mapTr<PromotionNewbieDto>({
				id: 'ID',
				fragranceId: 'Fragrance',
				order: 'Order',
			}),
		},
		FragranceListFilterFieldsDto: {
			...mapTr<FragranceListFilterFieldsDto>({
				active: 'Active',
				brandId: 'Brand',
				moderationStatus: 'Moderation status',
			}),
		},
		FragranceSetSuperDto: {
			...mapTr<FragranceSetSuperDto>({
				superFragranceId: 'Super fragrance ID',
			}),
		},
		FragranceOfferDto: {
			...mapTr<FragranceOfferDto>({
				id: 'ID',
				storeId: 'Store',
				available: 'Available',
				currency: 'Currency',
				price: 'Price',
				discountPrice: 'Discount Price',
				buyUrl: 'Buy URL',
				title: 'Title (optional)',
			}),
		},
		FragranceModerationDto: {
			...mapTr<FragranceModerationDto>({
				status: 'Moderation status',
				description: 'Description (optional)',
			}),
		},
		FragranceModerationStatus: {
			...mapTr<typeof FragranceModerationStatus>({
				DRAFT: 'DRAFT',
				PENDING: 'PENDING',
				APPROVED: 'APPROVED',
				REJECTED: 'REJECTED',
				ARCHIVED: 'ARCHIVED',
			}),
		},
		FragranceCreateDto: {
			...mapTr<FragranceCreateDto>({
				id: 'ID',
				name: 'Name',
				active: 'Active (visible to customers)',
				brandId: 'Brand',
				gender: 'Gender',
				description: 'Description',
				discontinued: 'Discontinued',
				longevity: 'Longevity',
				olfactives: 'Olfactive families',
				sillage: 'Sillage',
				years: 'Years',
				brandBaseNotes: 'Brand base notes',
				brandMidNotes: 'Brand mid notes',
				brandTopNotes: 'Brand top notes',
				noseNotes: 'Nose notes',
				noseRemindsFragrances: 'Nose Reminds Fragrances',
				perfumers: 'Perfumers',
				comment: 'Put some words about your changes',
				concentration: 'Concentration',
				noseWeather: 'Nose Weather',
				noseOccasion: 'Nose Occasion',
				noseSeason: 'Nose Season',
				noseLongevity: 'Nose Longevity',
				noseSillage: 'Nose Sillage',
				noseConcentration: 'Nose Concentration',
				hiddenPhotos: 'Hidden photos',
				privatePhotos: 'Private photos',
				publicPhotos: 'Public photos',
				links: 'Links',
				offers: 'Offers',
				ingredients: 'Ingredients',
				preliminaryPhotos: 'Preliminary photos',
				synonyms: 'Synonyms',
				asin: 'ASIN',
			}),
		},
		PerfumerCreateDto: {
			...mapTr<PerfumerCreateDto>({
				id: 'ID',
				active: 'Active (visible to customers)',
				company: 'Company',
				description: 'Description',
				images: 'Images',
				name: 'Name',
				comment: 'Put some words about your changes',
			}),
		},
		NoteCreateDto: {
			...mapTr<NoteCreateDto>({
				id: 'ID',
				active: 'Active (visible to customers)',
				images: 'Images',
				name: 'Name',
				synonyms: 'Synonyms',
				comment: 'Put some words about your changes',
			}),
		},
		FeedbackReplyDto: {
			...mapTr<FeedbackReplyDto>({
				imgLeft: 'Left Image URL (optional)',
				imgRight: 'Right Image URL (optional)',
				description: 'Description (optional)',
				message: 'Message',
				status: 'Status',
				title: 'Title',
				analyticsLabel: 'Analytics label (optional)',
				fragranceId: 'Fragrance Id (optional)',
				brandId: 'Brand Id (optional)',
			}),
		},
		UserMessageDto: {
			...mapTr<UserMessageDto>({
				imgLeft: 'Left Image URL (optional)',
				imgRight: 'Right Image URL (optional)',
				description: 'Description (optional)',
				message: 'Message',
				title: 'Title',
				analyticsLabel: 'Analytics label (optional)',
			}),
		},
		PushUserCriteria: {
			...mapTr<PushUserCondition>({
				userIds: 'ID of users to send to',
			}),
		},
		PushExperienceRatingCriteria: {
			...mapTr<PushExperienceRatingCondition>({
				percentOfSetRates: 'Percent of experiences with rates',
				hasNoExperiences: 'Send to users, who has no experiences at all',
			}),
		},
		PushFragranceShelvesCriteria: {
			...mapTr<PushFragranceShelvesCondition>({
				fragranceId: 'Fragrance ID',
				shelfIds: 'Shelves',
			}),
		},
		PushCampaignCreateDto: {
			...mapTr<PushCampaignCreateDto>({
				noticeType: 'Activity type',
				campaignName: 'Campaign name',
				description: 'Description (optional)',
				imgLeft: 'Left Image URL (optional)',
				imgRight: 'Right Image URL (optional)',
				message: 'Message',
				sendAt: 'Send At (LOCAL user time!)',
				sendNotice: 'Send to Activity',
				sendPush: 'Send push',
				sound: 'Sound',
				title: 'Title',
				vibrate: 'Vibrate',
				analyticsLabel: 'Analytics label',
				audienceConditions: 'Audience criterias',
				badge: 'Badge',
				light: 'Light',
				noticeTTL: 'Notice TTL, seconds',
				pushTTL: 'Push TTL, seconds',
				status: 'Status',
			}),
		},
		IngredientCreateDto: {
			...mapTr<IngredientCreateDto>({
				id: 'ID',
				casNumber: 'CAS number',
				description: 'Description',
				name: 'Name',
				images: 'Images',
				vendor: 'Vendor',
				analogs: 'Analogues',
				comment: 'Put some words about your changes',
			}),
		},
		SettingsDto: {
			...mapTr<SettingsDto>({
				fragranceSearchExcludeStrings: 'Exclude strings from fragrance names during search',
				imgBrandBig: 'Big brand image',
				imgBrandSmall: 'Small brand image',
				imgFragrance: 'Fragrance image',
				imgIngredient: 'Ingredient image',
				imgNote: 'Note image',
				imgPerfumer: 'Perfumer image',
				imgUser: 'User image',
				imgBrandBigDark: 'Big brand image',
				imgBrandSmallDark: 'Small brand image',
				imgFragranceDark: 'Fragrance image',
				imgIngredientDark: 'Ingredient image',
				imgNoteDark: 'Note image',
				imgPerfumerDark: 'Perfumer image',
				imgUserDark: 'User image',
				imgNotice: 'Notice image',
				imgNoticeDark: 'Notice image',
			}),
		},
		MaintenanceDto: {
			...mapTr<MaintenanceDto>({
				maintenanceStartDate: 'Start date',
				maintenanceEndDate: 'End date',
			}),
		},
		StopWordsDto: {
			...mapTr<StopWordsDto>({
				reserved: 'Reserved words',
				swear: 'Swear words',
			}),
		},
		Concentration: {
			...mapTr<typeof Concentration>({
				BODY_MIST: 'Body mist',
				EAU_DE_COLOGNE: 'Eau de cologne',
				EAU_DE_PARFUM: 'Eau de parfum',
				EAU_DE_TOILETTE: 'Eau de toilette',
				HAIR_MIST: 'Hair mist',
				OIL_PERFUME: 'Oil perfume',
				OTHER: 'Other',
				PERFUME: 'Perfume',
				SOLID_PERFUME: 'Solid perfume',
				EXTRAIT_DE_PARFUM: 'Extrait de Parfum',
			}),
		},
		Longevity: {
			...mapTr<typeof Longevity>({
				ABOUT_10_HOURS: '10 hours',
				ABOUT_1_HOUR: '1 hour',
				ABOUT_5_HOURS: '5 hours',
				LESS_THAN_HOUR: 'Less 1 hour',
				MORE_12_HOURS: 'More 12 hours',
			}),
		},
		Sillage: {
			...mapTr<typeof Sillage>({
				ARM_LENGTH: 'Arm length',
				CLOSE_TO_SKIN: 'Close to skin',
				FEW_METERS: 'Few metres',
				FILLS_ROOM: 'Fills room',
			}),
		},
		Gender: {
			...mapTr<typeof Gender>({
				FEMALE: 'Female',
				MALE: 'Male',
				UNISEX: 'Unisex',
			}),
		},
		Season: {
			...mapTr<typeof Season>({
				AUTUMN: 'Autumn',
				SPRING: 'Spring',
				SUMMER: 'Summer',
				WINTER: 'Winter',
			}),
		},
		Weather: {
			...mapTr<typeof Weather>({
				RAIN: 'Rain',
				SNOW: 'Snow',
				SUNNY: 'Sunny',
				WINDY: 'Windy',
			}),
		},
		Occasion: {
			...mapTr<typeof Occasion>({
				BEDTIME: 'Bedtime',
				DAILY: 'Everyday',
				LEASURE: 'Leisure',
				NIGHTLY: 'Nightly',
				OFFICE: 'Office',
				SPECIAL: 'Special occasion',
				SPORTS: 'Sports',
			}),
		},
		FragranceLinkType: {
			...mapTr<typeof FragranceLinkType>({
				ARTICLE: 'Article',
				VIDEO: 'Video',
				INTERNAL: 'Internal',
			}),
		},
	},
};

export default en;
